/* eslint-disable @typescript-eslint/no-explicit-any */

import { debounce, forEach } from 'es-toolkit/compat';
import { noop } from 'es-toolkit';

import { RoleType, core, isProto, safeToNumber } from '@tock/coretypes';

export const DEFAULT_AJAX_ERROR_MESSAGE = 'Failed to connect, check your internet and try again.';

// copypasta from shared/typings/index.d.ts
declare const BROWSER: boolean;
export interface ActiveAuth {
  businessGroupId: number;
  businessId: number;
  businessName: string;
  businessTypeString: string;
  businessProductType?: core.TockProductType;
  businessProductLevel?: core.TockProductLevel;
  roleType: RoleType;
  businessRole?: core.BusinessRoles.Role[];
}

// These are regexes so be careful not to use special characters
export const ignoredMessages = [
  '(unknown): Script error',
  "Can't find variable: inf",
  '\\[object Object\\]',
  '\\[object Response\\]',
  'unhandled rejection was null or undefined!',
  "Cannot read property 'getHostNode' of null",
  'Script error.',
  '__gCrWeb',
  'Failed to fetch',
  'Network request failed',
  'TypeError: cancelled',
  'TypeError: cancelado',
  'Batch request failed:',
  'The operation was aborted',
  'The network connection was lost',
  'Software caused connection abort	',
  'NetworkError when attempting to fetch resource.',
  "undefined is not an object (evaluating '__gCrWeb.autofill.extractForms')", // http://stackoverflow.com/questions/26483541/referenceerror-cant-find-variable-gcrweb
  "null is not an object (evaluating 'elt.parentNode')", // https://github.com/getsentry/raven-js/issues/756
  'Blocked a frame with origin(.*)from accessing(.*)origin "https://js.stripe.com"',
  'Blocked a frame with origin(.*)from accessing(.*)origin "https://staticxx.facebook.com"',
  "Couldn't activate business with id",
  'The operation is insecure',
  "'localStorage' property",
  '__firefox__',
  'Exception... "<no message>" nsresult: "0x805e0006',
  "'sessionStorage' property",
  'Loading chunk (.) failed',
  '\\{\\}',
  '"\\[unhandledrejection\\] error getting `reason` from event"',
  /Expected to be in state \w+ but was \w+/,
  /The OS was upgraded from Version .* to Version .*/i,
  'Cannot read property of undefined/null expression Qr',
  'Cannot read property of undefined/null expression window.webkit',
  "Uncaught TypeError: Cannot read properties of null (reading 'sessionId')",
  'TESTING: Chase error group',
];

/**
 * User agents/bots to exclude from sending data to Rollbar
 */
const excludedUserAgents = [
  'StatusCake_Pagespeed_Indev', // Monitoring tool using browsers from 2018/2019 :-/
];

export function checkIgnore(isUncaught: any, args: any, payload: any) {
  const isExcludedUserAgent = excludedUserAgents.includes(window?.navigator?.userAgent);

  if (isExcludedUserAgent) {
    return true;
  }


  // This is an attempt to avoid sending when we throw protos in `AjaxActions.sendError`, since they don't
  // get stringified to a useful error and they also don't have a stacktrace
  let anyProto = false;
  forEach(args, (maybeProto: any) => {
    anyProto = isProto(maybeProto) || anyProto;
  });
  return anyProto;
}

export function configureRollbarForPatron(patron: core.Patron, activeAuth: Partial<ActiveAuth>) {
  if (BROWSER && (window as any).Rollbar && patron?.id) {
    (window as any).Rollbar.configure({
      payload: {
        person: {
          businessId: activeAuth.businessId,
          businessGroupId: activeAuth.businessGroupId,
          id: safeToNumber(patron.id),
          email: patron.email,
        },
      },
    });
  }
}

interface RollbarTrace {
  exception?: {
    class: string;
    description?: string;
    message?: string;
  };
  frames?: any[];
}

export interface Payload {
  data?: { body?: { trace: RollbarTrace } };
  body?: { trace: RollbarTrace };
}
export function transformForDynamicSourcemap(
  payload: Payload,
  locRegex: RegExp,
  dynamicName: string
) {
  const trace = payload.data?.body?.trace ?? payload.body?.trace;
  for (const frame of trace?.frames ?? []) {
    const { filename } = frame;
    if (filename) {
      const m = filename.match(locRegex);
      if (m) {
        frame.filename = m[1] + `://${dynamicName}` + m[2];
      }
    }
  }
}

/**
 * Via side effects (sigh), transforms the rollbar payload to avoid
 * having errors sent that appear to just be connection exceptions.
 *
 * When there's an error in a reducer, rollbar can get confused about what
 * the correct error message should be and create an exception where the
 * title is `${DEFAULT_AJAX_ERROR_MESSAGE}`.
 * However, the message has more useful information, so we copy that over
 * as the description.
 *
 * @param {object} payload A payload that's headed for rollbar
 */
export function transformApiExceptionDescription(payload: Payload) {
  const exception = (payload.data?.body?.trace ?? payload.body?.trace)?.exception;

  if (exception?.description === DEFAULT_AJAX_ERROR_MESSAGE && exception.message) {
    exception.description = exception.message;
  }
}

export const alertSuspiciousContentInNotes = BROWSER
  ? debounce(
      (error: string) => (window as any).Rollbar.error(error),
      5 * 60 * 1000 // 5 minutes
    )
  : noop;
